import { sumBy } from 'lodash';
import {
  ActivitiesSheetFormat,
  KpiRowFormat,
  KpiSheetFormat,
  RowFormat,
  SCEligibility,
} from './table-generator';
import { FinancialsPerType } from './TaxonomyTableGenerator.hooks';
import { NonMaterialFinancials_ } from 'models/index.ts';

export const getTotalSubstantialContribution = (
  rows: RowFormat[],
  objectiveKey: keyof RowFormat,
  total: number
): { financial?: number } => {
  const financialsWithEligibility = rows
    .map((row) => row[objectiveKey] as { eligibility: SCEligibility; financial?: number })
    .filter((obj) => obj !== null && obj !== undefined);

  if (
    financialsWithEligibility.length === 0 ||
    financialsWithEligibility.every((obj) => obj.eligibility === SCEligibility.nonEligible)
  ) {
    return { financial: undefined };
  }

  const financials = financialsWithEligibility
    .map((obj) => obj.financial)
    .filter((f) => f !== null && f !== undefined);

  if (financials.length === 0) {
    return { financial: undefined };
  }

  return {
    financial: sumBy(financials, (f) => f!),
  };
};

export const getAlignedSumSubstantialContribution = (
  rows: RowFormat[],
  objectiveKey: keyof RowFormat
): { financial: number } => {
  return {
    financial: sumBy(rows, (row) => {
      const objectiveFinancials = row[objectiveKey] as { doubleCountedFinancial?: number };
      if (!!objectiveFinancials.doubleCountedFinancial) {
        return objectiveFinancials.doubleCountedFinancial;
      }
      return 0;
    }),
  };
};

export const getAlignedSumRow = (eligibleAlignedRows: RowFormat[]): RowFormat => {
  const row: RowFormat = {
    mitigationSC: getAlignedSumSubstantialContribution(eligibleAlignedRows, 'mitigationSC'),
    adaptationSC: getAlignedSumSubstantialContribution(eligibleAlignedRows, 'adaptationSC'),
    waterSC: getAlignedSumSubstantialContribution(eligibleAlignedRows, 'waterSC'),
    circularSC: getAlignedSumSubstantialContribution(eligibleAlignedRows, 'circularSC'),
    pollutionSC: getAlignedSumSubstantialContribution(eligibleAlignedRows, 'pollutionSC'),
    biodiversitySC: getAlignedSumSubstantialContribution(eligibleAlignedRows, 'biodiversitySC'),
  };
  return row;
};

export const getTotalRow = (
  rows: RowFormat[],
  total: FinancialsPerType,
  type: keyof FinancialsPerType
): RowFormat => {
  const fullTotal = total[type].total;
  const row: RowFormat = {
    eligiblePct: total[type].eligible / fullTotal,
    alignedAbsolute: total[type].aligned,
    alignedPct: total[type].aligned / fullTotal,
    mitigationSC: getTotalSubstantialContribution(rows, 'mitigationSC', fullTotal),
    adaptationSC: getTotalSubstantialContribution(rows, 'adaptationSC', fullTotal),
    waterSC: getTotalSubstantialContribution(rows, 'waterSC', fullTotal),
    circularSC: getTotalSubstantialContribution(rows, 'circularSC', fullTotal),
    pollutionSC: getTotalSubstantialContribution(rows, 'pollutionSC', fullTotal),
    biodiversitySC: getTotalSubstantialContribution(rows, 'biodiversitySC', fullTotal),
    enabling: sumBy(rows, (row) => (row.enabling as number) ?? 0),
    transitional: sumBy(rows, (row) => (row.transitional as number) ?? 0),
    alignedPctOfEligible: total[type].aligned / total[type].eligible,
  };
  console.log('row in totals.ts - type', type, row);
  return row;
};

export const calculateKpiSheetData = (
  turnoverSheet: ActivitiesSheetFormat,
  capexSheet: ActivitiesSheetFormat,
  opexSheet: ActivitiesSheetFormat,
  totalFinancials: FinancialsPerType,
  nonMaterialFinancials?: NonMaterialFinancials_
): KpiSheetFormat => {
  const convertActivityTotalRowToKpiRow = (row: RowFormat, kpiName: string): KpiRowFormat => {
    const financialsType: keyof FinancialsPerType =
      kpiName === 'turnover' ? 'revenue' : (kpiName as keyof FinancialsPerType);

    const total = totalFinancials?.[financialsType]?.total;
    const eligiblePct = (row.eligiblePct as number) ?? 0;
    const alignedAbsolute = (row.alignedAbsolute as number) ?? 0;
    const alignedPct = (row.alignedPct as number) ?? 0;
    const mitigationSC = row.mitigationSC?.financial;
    const adaptationSC = row.adaptationSC?.financial;
    const waterSC = row.waterSC?.financial;
    const circularSC = row.circularSC?.financial;
    const pollutionSC = row.pollutionSC?.financial;
    const biodiversitySC = row.biodiversitySC?.financial;
    const enablingPct = (row.enabling as number) ?? 0;
    const transitionalPct = (row.transitional as number) ?? 0;
    const notMaterialPct = (nonMaterialFinancials?.[financialsType] ?? 0) / (total ?? 1);

    return {
      kpiName,
      total,
      eligiblePct,
      alignedAbsolute,
      alignedPct,
      mitigationSC,
      adaptationSC,
      waterSC,
      circularSC,
      pollutionSC,
      biodiversitySC,
      enablingPct,
      transitionalPct,
      notMaterialPct,
    };
  };

  const turnoverRow: KpiRowFormat = convertActivityTotalRowToKpiRow(
    turnoverSheet.total,
    'turnover'
  );
  const capexRow: KpiRowFormat = convertActivityTotalRowToKpiRow(capexSheet.total, 'capex');
  const opexRow: KpiRowFormat = convertActivityTotalRowToKpiRow(opexSheet.total, 'opex');

  return {
    turnover: turnoverRow,
    capex: capexRow,
    opex: opexRow,
  };
};
